import { Boxes, Building2, DollarSign, MapPinned, TriangleAlert, Wrench } from "lucide-react";

const formatCurrency = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0);

export const OpsDashboard = ({ data, onOpenSite }) => {
  if (!data) return <div className="empty-state"><p className="font-mono text-sm">Loading operations dashboard...</p></div>;

  return (
    <div className="space-y-6">
      <section className="mb-2 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Operations</p>
          <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-zinc-900">Shared Operations Dashboard</h1>
          <p className="mt-2 text-sm text-zinc-500">Cross-business site, inventory, procurement, maintenance, and revenue view.</p>
        </div>
        <div className="border border-zinc-200 bg-white px-4 py-3 text-right">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Revenue Tracked</div>
          <div className="mt-1 text-2xl font-black text-zinc-900">{formatCurrency(data.revenue_total || 0)}</div>
        </div>
      </section>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-5">
        <StatCard icon={<MapPinned size={18} />} label="Sites" value={data.totals?.sites || 0} />
        <StatCard icon={<Building2 size={18} />} label="Suppliers" value={data.totals?.suppliers || 0} />
        <StatCard icon={<Boxes size={18} />} label="Inventory Items" value={data.totals?.inventory_items || 0} />
        <StatCard icon={<Wrench size={18} />} label="Open Work Orders" value={data.totals?.open_work_orders || 0} />
        <StatCard icon={<TriangleAlert size={18} />} label="Low Stock" value={data.totals?.low_stock_items || 0} />
      </div>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="card p-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Site Performance</h2>
          <div className="mt-4 space-y-3">
            {(data.site_metrics || []).length === 0 ? <p className="text-sm text-zinc-400">No operations sites yet.</p> : (data.site_metrics || []).map((site) => (
              <button className="block w-full border border-zinc-200 bg-white p-4 text-left hover:border-zinc-400" key={site.site_id} onClick={() => onOpenSite?.(site.site_id)} type="button">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold text-zinc-900">{site.site_name}</div>
                    <div className="mt-1 text-sm text-zinc-500">{site.company_name} · {site.business_name} · {site.business_type}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-right text-sm text-zinc-600">
                    <span>{site.sessions} sessions</span>
                    <span>{formatCurrency(site.revenue)}</span>
                    <span>{site.assets} assets</span>
                    <span>{site.low_stock_items} low stock</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <ListCard title="Critical Missing Quotes" items={data.critical_missing_quotes} renderItem={(item) => (
            <div>
              <div className="font-semibold text-zinc-900">{item.item_name}</div>
              <div className="mt-1 text-sm text-zinc-500">{item.specification || item.unit || "No details"}{item.is_critical ? " · Critical" : ""}</div>
            </div>
          )} />
          <ListCard title="Low Stock Alerts" items={data.low_stock_items} renderItem={(item) => (
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-semibold text-zinc-900">{item.name}</div>
                <div className="mt-1 text-sm text-zinc-500">{item.item_role || "item"} · {item.category || "General"}</div>
              </div>
              <div className="text-right text-sm text-zinc-600">
                <div>{item.quantity_on_hand} {item.unit}</div>
                <div>Reorder {item.reorder_level}</div>
              </div>
            </div>
          )} />
          <ListCard title="Upcoming Maintenance" items={data.upcoming_maintenance} renderItem={(item) => (
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-semibold text-zinc-900">{item.title}</div>
                <div className="mt-1 text-sm text-zinc-500">{item.priority || "Medium"} priority · {item.maintenance_type || "General"}</div>
              </div>
              <div className="text-right text-sm text-zinc-600">{item.days_until ?? "?"} days</div>
            </div>
          )} />
        </div>
      </section>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="stat-card">
    <div className="mb-2 flex items-center justify-between"><span className="stat-label">{label}</span><span className="text-zinc-400">{icon}</span></div>
    <div className="stat-value">{value}</div>
  </div>
);

const ListCard = ({ title, items = [], renderItem }) => (
  <section className="card p-6">
    <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">{title}</h2>
    <div className="mt-4 space-y-3">
      {items?.length ? items.map((item, index) => <div className="border border-zinc-200 bg-white p-4" key={item.id || index}>{renderItem(item)}</div>) : <p className="text-sm text-zinc-400">Nothing to show.</p>}
    </div>
  </section>
);
